import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  FormControlLabel,
  Skeleton,
  Stack,
  styled,
  SwipeableDrawer,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography
} from "@mui/material";
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { DriveTimeOptimizationResource, SkippedVisitResource } from "~/hooks/visitDragDropReducer";
import InfoIcon from '@mui/icons-material/InfoOutlined';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { useZonedDateFormat } from "~/providers/ZonedDateFormatProvider";
import { useOrgConfig } from "~/providers/OrgConfigProvider";
import { CommandIndexProvidersQuery } from "~/graphql/__generated__/graphql";
import { grey } from "@mui/material/colors";
import { TabContext, TabPanel } from "@mui/lab";
import { Permission, useHasPermission } from "~/hooks/permissions";
import { OptimizationIssueStats } from "./optimizationReviewUtils";
import { StatusBgMap, StatusForegroundMap } from "~/utils/statuses";

interface OptimizationReviewProps {
  providers: CommandIndexProvidersQuery["commandIndexProviders"]["providers"];
  driveTimeOptimizations?: DriveTimeOptimizationResource[];
  loading: boolean;
  issueStats: OptimizationIssueStats;
  showGhosts: boolean;
  skippedVisitResources?: SkippedVisitResource[];
  onToggleGhosts: (show: boolean) => void;
  optimizerLogId?: number | null;
}

const formatMinutes = (totalMinutes: number): string => {
  const normalizedMinutes = Math.max(0, Math.round(totalMinutes));
  if (normalizedMinutes === 0) return "0 min";

  const hours = Math.floor(normalizedMinutes / 60);
  const minutes = normalizedMinutes % 60;

  const parts = [];
  if (hours) parts.push(`${hours} ${hours === 1 ? "hr" : "hrs"}`);
  if (minutes) parts.push(`${minutes} ${minutes === 1 ? "min" : "min"}`);

  return parts.join(" ");
};

const StyledBox = styled('div')(({ theme }) => ({
  backgroundColor: '#fff',
  ...theme.applyStyles('dark', {
    backgroundColor: grey[800],
  }),
}));

const drawerBleeding = 56;

const Puller = styled('div')(({ theme }) => ({
  width: 30,
  height: 6,
  backgroundColor: grey[300],
  borderRadius: 3,
  position: 'absolute',
  top: 8,
  left: 'calc(50% - 15px)',
  ...theme.applyStyles('dark', {
    backgroundColor: grey[900],
  }),
}));

const OptimizationReview = ({
  loading,
  issueStats,
  driveTimeOptimizations,
  providers,
  showGhosts,
  onToggleGhosts,
  skippedVisitResources,
  optimizerLogId,
}: OptimizationReviewProps) => {
  const orgConfig = useOrgConfig();
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const { timeLabelWithZone } = useZonedDateFormat();

  const hasDebugPermission = useHasPermission(Permission.debugApps);
  const container = window !== undefined ? () => window.document.body : undefined;

  const [value, setValue] = useState('optimizations');

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  const {
    consolidatedVisitResourceIssues,
    totalOptimizedVisits,
    newTotalDriveTime,
    originalTotalDriveTime,
    newAvailable60Min,
    originalAvailable60Min,
    newVisitsWithIssuesCount,
    originalVisitsWithIssuesCount,
  } = issueStats;

  return (
    <>
      <Box sx={{ mr: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Optimization Review
          <Button
            sx={{
              py: 0,
              fontSize: 11,
              ml: 2
            }}
            ref={anchorRef}
            variant='outlined'
            size='small'
            onClick={() => setOpen(!open) }
          >View optimizations/issues</Button>
          {hasDebugPermission && optimizerLogId && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5, pr: 1.5 }}>
              Log ID: <Link to={`/optimizer-log/${optimizerLogId}`} target="_blank">{optimizerLogId}</Link>
            </Typography>
          )}
        </Typography>
        <Stack direction='row' gap={3}>
          <Box sx={{ maxWidth: 100, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography sx={{
                cursor: 'pointer',
                borderRadius: 60,
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                lineHeight: 1.2
              }}
              variant="subtitle2"
              color="text.secondary"
            >
              <span>Total visits reassigned</span>
              <Tooltip title="The total number of visits that have been relocated or assigned to different time slots or provider during the optimization process.">
                <InfoIcon fontSize={"small"} sx={{ verticalAlign: 'baseline', width: 18 }} color="primary" />
              </Tooltip>
            </Typography>
            <Typography variant="h6">
              {(loading ? <Skeleton variant="rectangular" height={15} width={45} /> : totalOptimizedVisits) ?? '-'}
            </Typography>
            <Typography color='gray' variant="caption">
              (Skipped: {skippedVisitResources?.length ?? '-'})
            </Typography>
          </Box>
          <Box sx={{ maxWidth: 140, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography sx={{
                cursor: 'pointer',
                borderRadius: 60,
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                lineHeight: 1.2
              }}
              variant="subtitle2"
              color="text.secondary"
              ref={anchorRef}
            >
              <span>New Total Drive Time</span>
              <Tooltip title="Shows drive time after optimization">
                <InfoIcon fontSize={"small"} sx={{ verticalAlign: 'baseline', width: 18 }} color="primary" />
              </Tooltip>
            </Typography>
            <Typography variant="h6" sx={{ lineHeight: 1 }}>
              {newTotalDriveTime ? ((loading ? <Skeleton variant="rectangular" height={15} width={45} /> : formatMinutes(newTotalDriveTime))) : '--'}
            </Typography>
            <Typography color='gray' variant="caption">
              (Original: {!loading ? formatMinutes(originalTotalDriveTime) : '-'})
            </Typography>
          </Box>

          <Box sx={{ maxWidth: 120, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography sx={{
                cursor: 'pointer',
                borderRadius: 60,
                display: 'flex',
                justifyContent: 'space-around',
                alignItems: 'center',
                lineHeight: 1.2
              }}
              variant="subtitle2"
              color="text.secondary"
              ref={anchorRef}
            >
              <span>New available 60 min slots</span>
              <Tooltip title="Shows the number of newly available unscheduled time slots after optimization.">
                <InfoIcon fontSize={"small"} sx={{ verticalAlign: 'baseline', width: 18 }} color="primary" />
              </Tooltip>
            </Typography>
            <Typography variant="h6">
              {loading ? <Skeleton variant="rectangular" height={15} width={45} /> : newAvailable60Min}
            </Typography>
            <Typography color='gray' variant="caption">
              (Original: {!loading ? originalAvailable60Min : '--'})
            </Typography>
          </Box>

          <Box sx={{ maxWidth: 120, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography sx={{
                cursor: 'pointer',
                borderRadius: 60,
                display: 'flex',
                justifyContent: 'space-around',
                alignItems: 'center',
                lineHeight: 1.2
              }}
              variant="subtitle2"
              color="text.secondary"
              ref={anchorRef}
            >
              <span>Issues</span>
              <Tooltip title="Shows the number of visits with overlaps or warnings after optimize.">
                <InfoIcon fontSize={"small"} sx={{ verticalAlign: 'baseline', width: 18 }} color="primary" />
              </Tooltip>
            </Typography>
            <Typography variant="h6">
              {loading ? <Skeleton variant="rectangular" height={15} width={45} /> : newVisitsWithIssuesCount}
            </Typography>
            <Typography color='gray' variant="caption">
              (Original: {!loading ? originalVisitsWithIssuesCount : '--'})
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Stack
        direction="column"
        spacing={1.5}
        alignItems="center"
        sx={{ mr: 2, flexShrink: 0 }}
      >
        {driveTimeOptimizations?.length ? (
          <>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: 1,
                py: 0.25,
                borderRadius: 2,
                bgcolor: 'grey.100',
              }}
            >
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: 0.5,
                  opacity: 0.4,
                  bgcolor: StatusBgMap["scheduled"],
                  border: `1px solid ${StatusForegroundMap["scheduled"]}`,
                  mr: 0.75,
                }}
              />
              <Typography variant="caption">Original position</Typography>
            </Box>

            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Switch
                  size="small"
                  checked={showGhosts}
                  onChange={(e) => onToggleGhosts(e.target.checked)}
                />
              }
              label={
                <Tooltip title="Show/hide original positions on mouse hover">
                  <>
                  <Typography variant="caption">
                    {showGhosts ? 'Show originals' : 'Hide originals'}
                  </Typography>
                  <InfoIcon fontSize={"small"} sx={{ verticalAlign: 'middle', width: 18 }} color="primary" />
                  </>
                </Tooltip>
              }
            />
          </>
        ) : null}
        <SwipeableDrawer
          container={container}
          anchor="bottom"
          open={open}
          onClose={() => setOpen(false)}
          onOpen={() => setOpen(true)}
          swipeAreaWidth={drawerBleeding}
          disableSwipeToOpen={true}
          keepMounted
        >
          <StyledBox
            sx={{
              position: 'absolute',
              top: -drawerBleeding,
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
              visibility: 'visible',
              right: 0,
              left: 0,
            }}
          >
            <Puller />
            <Typography sx={{ p: 2, color: 'text.secondary' }}>
              {value === 'optimizations' 
                ? `${driveTimeOptimizations?.length || 0} results`
                : `${consolidatedVisitResourceIssues.length} results`}
            </Typography>
          </StyledBox>
          <StyledBox sx={{ px: 2, pb: 2, height: '100%', overflow: 'auto' }}>
            <Card elevation={0}>
              <CardActions sx={{ display: 'flex', justifyContent: "end", borderTop: 1, borderColor: "divider" }}>
                <Button
                  variant="outlined"
                  onClick={() => setOpen(false)}
                >
                  Close
                </Button>
              </CardActions>
              <CardContent>
                <TabContext value={value}>
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                      <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
                        <Tab label="Optimized" value='optimizations' />
                        <Tab label="Skipped/Issues" value='skipped' />
                      </Tabs>
                    </Box>
                    <TabPanel value={'optimizations'}>
                      {driveTimeOptimizations?.length ? (
                        <Box sx={{
                          maxHeight: 300,
                          overflowY: 'scroll'
                        }}>
                          <Table aria-label="simple table">
                            <TableHead>
                              <TableRow>
                                <TableCell align="right">Patient</TableCell>
                                <TableCell align="right">Visit Type</TableCell>
                                <TableCell align="right">Original visit time</TableCell>
                                <TableCell align="right">New visit time</TableCell>
                                <TableCell align="right">Original Provider</TableCell>
                                <TableCell align="right">New Provider (if any)</TableCell>
                                <TableCell align="right">Original Drive time</TableCell>
                                <TableCell align="right">New Drive time</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {driveTimeOptimizations?.map((opt, i) => {
                                if (!opt.patient?.firstName) return null;
                                const originalProvider = providers.find((p) => p.id === opt?.originalProviderId);
                                const optProvider = providers.find((p) => p.id === opt?.newProviderId);
    
                                return (
                                  <TableRow
                                    key={`${opt.originalVisit.startTime}-${i}`}
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                  >
                                    <TableCell align="right">{opt?.patient?.firstName} {opt?.patient.lastName}</TableCell>
                                    <TableCell align="right">{opt.originalVisit.visitTypeId ? orgConfig.visitTypes[opt.originalVisit.visitTypeId]?.name : null}</TableCell>
                                    <TableCell align="right">
                                      {timeLabelWithZone(opt.originalVisit.startTime || "", false, opt.originalVisit.addressTimezone)}
                                    </TableCell>
                                    <TableCell align="right">
                                      {timeLabelWithZone(opt.optimizedVisitStartTime || "", false, opt.optimizedVisit.addressTimezone)}
                                    </TableCell>
                                    <TableCell align="right">{originalProvider?.firstName} {originalProvider?.lastName}</TableCell>
                                    <TableCell align="right">{optProvider?.firstName} {optProvider?.lastName}</TableCell>
                                    <TableCell align="right">{opt.originalDriveTime} min</TableCell>
                                    <TableCell align="right">{opt.newDriveTime} min</TableCell>
                                  </TableRow>
                                )
                              })}
                            </TableBody>
                          </Table>
                        </Box>
                      ) : (
                        <Typography variant="subtitle1">No optimized visits</Typography>
                      )}
                    </TabPanel>
                    <TabPanel value={'skipped'}>
                      {consolidatedVisitResourceIssues.length ? (
                        <Box sx={{
                          maxHeight: 300,
                          overflowY: 'scroll'
                        }}>
                          <Table aria-label="simple table">
                            <TableHead>
                              <TableRow>
                                <TableCell align="left">Reason(s)</TableCell>
                                <TableCell align="right">Patient</TableCell>
                                <TableCell align="right">Visit Type</TableCell>
                                <TableCell align="right">Visit time</TableCell>
                                <TableCell align="right">Provider</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {consolidatedVisitResourceIssues.map((issue, i) => {
                                const vr = issue.visitResource;
                                if (!vr.visit?.patient?.firstName) return null;
                                const provider = providers.find((p) => p.id === vr?.providerId);

                                return (
                                  <TableRow
                                    key={`${vr.id}-${i}`}
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                  >
                                    <TableCell align="right">
                                      {issue.mainText ? (
                                        <Tooltip
                                          arrow
                                          slotProps={{
                                            tooltip: {
                                              sx: {
                                                bgcolor: "white",
                                                color: "text.primary",
                                                border: "1px solid",
                                                borderColor: "divider",
                                                boxShadow: 3,
                                                borderRadius: 1,
                                                overflow: "visible",
                                              },
                                            },
                                            arrow: { sx: { color: "white" } },
                                          }}
                                          title={
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                              {issue.allReasons.map((reason, idx) => (
                                                <Typography key={idx} variant="body2">{reason}</Typography>
                                              ))}
                                            </Box>
                                          }
                                        >
                                          <Box sx={{ display: 'flex', cursor: 'pointer', alignItems: 'center', gap: 0.5 }}>
                                            <EventBusyIcon color="error" fontSize="small" />
                                            <Typography variant="body2">
                                              {issue.mainText}
                                              {issue.additionalCount > 0 && ` +${issue.additionalCount}`}
                                            </Typography>
                                          </Box>
                                        </Tooltip>
                                      ) : (
                                        <Typography variant="body2">-</Typography>
                                      )}
                                    </TableCell>
                                    <TableCell align="right">{vr?.visit?.patient?.firstName} {vr?.visit?.patient.lastName}</TableCell>
                                    <TableCell align="right">{vr.visit.visitTypeId ? orgConfig.visitTypes[vr.visit.visitTypeId]?.name : null}</TableCell>
                                    <TableCell align="right">
                                      {timeLabelWithZone(vr.startTime || "", false, vr.visit.addressTimezone)}
                                    </TableCell>
                                    <TableCell align="right">{provider?.firstName} {provider?.lastName}</TableCell>
                                  </TableRow>
                                )
                              })}
                            </TableBody>
                          </Table>
                        </Box>
                      ) : (
                        <Typography variant="subtitle1">No issues/skipped visits</Typography>
                      )}
                    </TabPanel>
                  </Box>
                </TabContext>
              </CardContent>
            </Card>
          </StyledBox>
        </SwipeableDrawer>
      </Stack>
    </>
  );
};

export default OptimizationReview;
